/* Common Imports */

import React from "react";
import { makeStyles } from "@material-ui/core/styles";

/* Component Imports */

import { Paper, Typography, IconButton } from "@material-ui/core";
import PICButtons from "./sub-component/PICButtons";
import PICAreaUnit from "./sub-component/PICAreaUnit";
import PICChips from "./sub-component/PICChips";
import Skeleton from "@material-ui/lab/Skeleton";

/* Data Imports */

import AppConfig from "../../../AppConfig";

/* Define all styles */

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: "1.5rem",
    marginBottom: "2rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    width: "100%",
  },
  titleRow: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  title: {
    color: "#202020",
    fontWeight: "500",
    fontSize: "1.5rem",
    lineHeight: "2rem",
    marginBottom: "0.28rem",
  },
  iconTextRow: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    marginBottom: "0.28rem",
  },
  locationBuilderText: {
    color: "#666666",
    fontWeight: 300,
    fontSize: "1.05rem",
    lineHeight: "1.688rem",
    marginLeft: "0.5rem",
  },
  priceText: {
    color: "#202020",
    fontWeight: 500,
    fontSize: "1.1rem",
    lineHeight: "1.688rem",
    marginLeft: "0.5rem",
  },
  description: {
    color: "#666666",
    fontWeight: 400,
    fontSize: "1rem",
    lineHeight: "1.5rem",
    marginBottom: "1rem",
  },
}));

const PropertyInfoCard = (props) => {
  const classes = useStyles();

  const reportButtonClickListener = (event) => {
    event.preventDefault();
    window.location.hash = "#report-" + props.slug;
  };

  if (!props.loading) {
    return (
      <React.Fragment>
        <Paper elevation={2} className={classes.paper}>
          {/* Title and IconButton */}
          <div className={classes.titleRow}>
            <Typography
              variant="body2"
              component="h1"
              className={classes.title}
            >
              {props.title ? props.title : "Property Title"}
            </Typography>
            {!props.hideButtons ? (
              <div className={classes.iconButtons}>
                <IconButton
                  aria-label="Report"
                  onClick={reportButtonClickListener}
                  size="small"
                >
                  <img
                    alt={AppConfig.imageAltText}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    height={24}
                    width={24}
                    src="/images/icons/action/report.svg"
                  />
                </IconButton>
              </div>
            ) : undefined}
          </div>

          {/* Location, Builder and Price */}
          <div className={classes.iconTextRow}>
            <img
              alt={AppConfig.imageAltText}
              loading="lazy"
              referrerPolicy="no-referrer"
              height={24}
              width={24}
              src="/images/icons/social/location.svg"
            />
            <span className={classes.locationBuilderText}>
              {props.location ? props.location : "Property Location"}
            </span>
          </div>
          <div className={classes.iconTextRow}>
            <img
              alt={AppConfig.imageAltText}
              loading="lazy"
              referrerPolicy="no-referrer"
              height={24}
              width={24}
              src="/images/icons/config/builder.svg"
            />
            <span className={classes.locationBuilderText}>
              {props.builder ? props.builder : "Property Builder"}
            </span>
          </div>
          <div className={classes.iconTextRow}>
            <img
              alt={AppConfig.imageAltText}
              loading="lazy"
              referrerPolicy="no-referrer"
              height={24}
              width={24}
              src="/images/icons/config/rupee.svg"
            />
            <span className={classes.priceText}>
              {props.price ? props.price : "Property Price"}
            </span>
          </div>

          {/* Short Description */}
          <Typography
            variant="body2"
            component="h1"
            className={classes.description}
          >
            {props.description ? props.description : "Property Description"}
          </Typography>

          {/* Chips */}
          <PICChips chips={props.chips} />

          {/* Area & Unit */}
          <PICAreaUnit
            unitIcon={props.unitIcon}
            area={props.area}
            config={props.config}
          />

          {/* Share & Download Buttons */}
          {!props.hideButtons ? (
            <PICButtons
              slug={props.slug}
              pdfHref={props.pdfhref}
              title={props.title ? props.title : "Unknown Property"}
              pdfButtonClickListener={
                typeof props.pdfButtonClickListener === "function"
                  ? props.pdfButtonClickListener
                  : pdfButtonClickListener
              }
            />
          ) : undefined}
        </Paper>
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <Paper elevation={2} className={classes.paper}>
          {/* Title and IconButton */}
          <div className={classes.titleRow}>
            <Skeleton>
              <Typography
                variant="body2"
                component="h1"
                className={classes.title}
              >
                {props.title ? props.title : "Property Title"}
              </Typography>
            </Skeleton>
            <Skeleton>
              <div className={classes.iconButtons}>
                <IconButton
                  aria-label="Report"
                  onClick={reportButtonClickListener}
                  size="small"
                >
                  <img
                    alt={AppConfig.imageAltText}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    height={24}
                    width={24}
                    src="/images/icons/action/report.svg"
                  />
                </IconButton>
              </div>
            </Skeleton>
          </div>

          {/* Location, Builder and Price */}
          <Skeleton>
            <div className={classes.iconTextRow}>
              <img
                alt={AppConfig.imageAltText}
                loading="lazy"
                referrerPolicy="no-referrer"
                height={24}
                width={24}
                src="/images/icons/social/location.svg"
              />
              <span className={classes.locationBuilderText}>
                {props.location ? props.location : "Property Location"}
              </span>
            </div>
          </Skeleton>
          <Skeleton>
            <div className={classes.iconTextRow}>
              <img
                alt={AppConfig.imageAltText}
                loading="lazy"
                referrerPolicy="no-referrer"
                height={24}
                width={24}
                src="/images/icons/config/builder.svg"
              />
              <span className={classes.locationBuilderText}>
                {props.builder ? props.builder : "Property Builder"}
              </span>
            </div>
          </Skeleton>
          <Skeleton>
            <div className={classes.iconTextRow}>
              <img
                alt={AppConfig.imageAltText}
                loading="lazy"
                referrerPolicy="no-referrer"
                height={24}
                width={24}
                src="/images/icons/config/rupee.svg"
              />
              <span className={classes.priceText}>
                {props.price ? props.price : "Property Price"}
              </span>
            </div>
          </Skeleton>

          {/* Short Description */}
          <Skeleton>
            <Typography
              variant="body2"
              component="h1"
              className={classes.description}
            >
              {props.description ? props.description : "Property Description"}
            </Typography>
          </Skeleton>

          {/* Chips */}
          <PICChips chips={props.chips} />

          {/* Area & Unit */}
          <Skeleton>
            <PICAreaUnit
              unitIcon={props.unitIcon}
              area={props.area}
              config={props.config}
            />
          </Skeleton>

          {/* Share & Download Buttons */}
          <PICButtons
            slug={props.slug}
            pdfHref={props.pdfhref}
            title={props.title ? props.title : "Unknown Property"}
            pdfButtonClickListener={
              typeof props.pdfButtonClickListener === "function"
                ? props.pdfButtonClickListener
                : pdfButtonClickListener
            }
          />
        </Paper>
      </React.Fragment>
    );
  }
};

export default PropertyInfoCard;
